import { NO, YES } from '../../applicantStore/types/YesOrNo';
import useApplicantStore from '../../applicantStore/useApplicantStore';
import useCoapplicantStore from '../../coapplicantStore/useCoapplicantStore';
import useDashboardStore from '../../dashboardStore/useDashboardStore';
import { WorkflowPage } from '../types/WorkflowPage';

export const applicantWorkflow: WorkflowPage[] = [
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}`;
    },
  },
  {
    completed(): boolean {
      const { currentResidenceType } = useApplicantStore.getState();

      return Boolean(currentResidenceType);
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/residence-type`;
    },
  },
  {
    completed(): boolean {
      const {
        computed: { isCurrentResidenceDetailsCompleted },
      } = useApplicantStore.getState();

      return isCurrentResidenceDetailsCompleted;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/residence-details`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/residence-duration`;
    },
  },
  {
    condition(): boolean {
      const {
        computed: { isCurrentResidenceLessThan2Years },
      } = useApplicantStore.getState();

      return isCurrentResidenceLessThan2Years;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/previous-residence`;
    },
  },
  {
    completed(): boolean {
      const { isCurrentlyEmployed } = useApplicantStore.getState();

      return Boolean(isCurrentlyEmployed);
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/employment-status`;
    },
  },
  {
    completed(): boolean {
      const { isCurrentlyEmployed, currentIncomeEmploymentType } =
        useApplicantStore.getState();

      // Only check completion if this page is actually in the workflow path
      // Page is not applicable, so consider it "completed"
      if (isCurrentlyEmployed !== YES) {
        return true;
      }

      return Boolean(currentIncomeEmploymentType);
    },
    condition(): boolean {
      const { isCurrentlyEmployed } = useApplicantStore.getState();
      return isCurrentlyEmployed === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/employment-type`;
    },
  },
  {
    completed(): boolean {
      const { isCurrentlyEmployed, currentIncomeEmploymentType } =
        useApplicantStore.getState();

      // Only check completion if this page is actually in the workflow path
      // Page is not applicable, so consider it "completed"
      if (isCurrentlyEmployed !== NO) {
        return true;
      }

      return Boolean(currentIncomeEmploymentType);
    },
    condition(): boolean {
      const { isCurrentlyEmployed } = useApplicantStore.getState();
      return isCurrentlyEmployed === NO;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/unemployment-type`;
    },
  },
  {
    completed(): boolean {
      const {
        isCurrentlyEmployed,
        computed: { isCurrentIncomeEmployedDetailsCompleted },
      } = useApplicantStore.getState();

      // Only check completion if this page is actually in the workflow path
      // Page is not applicable, so consider it "completed"
      if (isCurrentlyEmployed !== YES) {
        return true;
      }

      return isCurrentIncomeEmployedDetailsCompleted;
    },
    condition(): boolean {
      const { isCurrentlyEmployed } = useApplicantStore.getState();
      return isCurrentlyEmployed === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/employment-details`;
    },
  },
  {
    completed(): boolean {
      const {
        isCurrentlyEmployed,
        computed: { isCurrentIncomeUnemployedDetailsCompleted },
      } = useApplicantStore.getState();

      // Only check completion if this page is actually in the workflow path
      // Page is not applicable, so consider it "completed"
      if (isCurrentlyEmployed !== NO) {
        return true;
      }

      return isCurrentIncomeUnemployedDetailsCompleted;
    },
    condition(): boolean {
      const { isCurrentlyEmployed } = useApplicantStore.getState();
      return isCurrentlyEmployed === NO;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/unemployment-details`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/employment-duration`;
    },
  },
  {
    condition(): boolean {
      const {
        computed: { isCurrentIncomeLessThan1Year },
      } = useApplicantStore.getState();

      return isCurrentIncomeLessThan1Year;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/previous-employment`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/additional-income`;
    },
  },
  {
    condition(): boolean {
      const { hasAdditionalIncome } = useApplicantStore.getState();
      return hasAdditionalIncome === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/income-details`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/add-coapplicant`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-personal-relationship`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-personal-details`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-residence-relationship`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { hasSameResidenceAsApplicant } = useCoapplicantStore.getState();
      return addCoapplicant === YES && hasSameResidenceAsApplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-same-residence`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { hasSameResidenceAsApplicant } = useCoapplicantStore.getState();
      return addCoapplicant === YES && hasSameResidenceAsApplicant === NO;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-different-residence-type`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { hasSameResidenceAsApplicant } = useCoapplicantStore.getState();
      return addCoapplicant === YES && hasSameResidenceAsApplicant === NO;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-different-residence`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-residence-duration`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const {
        computed: { isCurrentResidenceLessThan2Years },
      } = useCoapplicantStore.getState();

      return addCoapplicant === YES && isCurrentResidenceLessThan2Years;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-previous-residence`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-employment-status`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { isCoapplicantCurrentlyEmployed } = useCoapplicantStore.getState();
      return addCoapplicant === YES && isCoapplicantCurrentlyEmployed === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-employment-type`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { isCoapplicantCurrentlyEmployed } = useCoapplicantStore.getState();
      return addCoapplicant === YES && isCoapplicantCurrentlyEmployed === NO;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-unemployment-type`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { isCoapplicantCurrentlyEmployed } = useCoapplicantStore.getState();
      return addCoapplicant === YES && isCoapplicantCurrentlyEmployed === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-employment-details`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const { isCoapplicantCurrentlyEmployed } = useCoapplicantStore.getState();
      return addCoapplicant === YES && isCoapplicantCurrentlyEmployed === NO;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-unemployment-details`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-employment-duration`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      const {
        computed: { isCurrentIncomeLessThan1Year },
      } = useCoapplicantStore.getState();

      return addCoapplicant === YES && isCurrentIncomeLessThan1Year;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-previous-employment`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-additional-income`;
    },
  },
  {
    condition(): boolean {
      const { hasAdditionalIncome } = useCoapplicantStore.getState();
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES && hasAdditionalIncome === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-income-details`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/review-applicant`;
    },
  },
  {
    condition(): boolean {
      const { addCoapplicant } = useApplicantStore.getState();
      return addCoapplicant === YES;
    },
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/coapp-review-applicant`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/submit-application`;
    },
  },
  {
    get path(): string {
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();
      return `/applicant/${vehicleId}/application-success`;
    },
  },
];
